import { useCallback, useMemo } from 'react';
import { useImmer } from 'use-immer';

import type { AuthFormField } from './types';

type Values = Record<string, AuthFormField['value']>;
type Fields = Record<string, AuthFormField>;

export interface AuthStore {
    add: (name: string, data: AuthFormField) => void;
    remove: (name: string) => void;
    getField: (name: string) => AuthFormField;
    getValue: (name: string) => string;
    updateField: (name: string, data: AuthFormField) => void;
    hasError: boolean;
    hasTouched: boolean;
}

const getHasError = (fields: Fields): boolean => {
    let hasError = false;
    for (const name of Object.keys(fields)) {
        if (fields[name]?.error) {
            hasError = true;
            break;
        }
    }
    return hasError;
};

const getValues = (fields: Fields): Values => {
    const values = {};
    for (const name of Object.keys(fields)) {
        values[name] = fields[name]?.value;
    }
    return values;
};

const getHasTouched = (fields: Fields): boolean => Object.keys(fields).some((k) => fields[k].touched);

const validateField = (fields: Fields, name: string, values: Values): void => {
    const field = fields[name];
    const rulesInput = field?.rules;

    if (rulesInput) {
        const rules = Array.isArray(rulesInput) ? rulesInput : [rulesInput];
        for (const rule of rules) {
            field.error = rule(values[name], values);
            if (field.error) {
                break;
            }
        }
    }
};

// Поле могло ещё не зарегистрироваться (сабмит раньше add) — не падаем (FRONT-2AB9).
const getValue = (fields: Fields, name: string): string => fields[name]?.value ?? '';

export const useAuthStore = (): AuthStore => {
    const [fields, setFields] = useImmer<Fields>({});
    const [valid, setValid] = useImmer<boolean>(true);

    const updateField = useCallback(
        (name: string, data: Partial<AuthFormField>): void => {
            setFields((draft) => {
                if (typeof draft[name] !== 'undefined') {
                    draft[name] = {
                        ...draft[name],
                        ...data,
                    };
                    draft[name].touched = true;
                    validateField(draft, name, getValues(draft));
                }
                for (const f of Object.values(draft)) {
                    const valid = !f.error;
                    setValid(valid);
                    if (!valid) break;
                }
            });
        },
        [setFields, setValid],
    );

    return useMemo(
        () => ({
            valid,
            get hasError() {
                return getHasError(fields);
            },
            get hasTouched() {
                return getHasTouched(fields);
            },
            getValue: (name: string) => getValue(fields, name),
            getField: (name: string) => fields[name] || {},
            updateField,
            add: (name: string, data: Partial<AuthFormField>) => {
                setFields((draft) => {
                    // Копируем data: в стор не должен попадать внешний объект, который immer
                    // замораживает — иначе повторная валидация падает на записи field.error
                    // («Cannot assign to read only property 'error'», FRONT-2AAN/2AAF).
                    // updateField по той же причине собирает новое значение через спред.
                    draft[name] = { ...data };
                    validateField(draft, name, getValues(draft));
                });
            },
            remove: (name: string) => {
                setFields((draft) => {
                    delete draft[name];
                });
            },
        }),
        [fields, setFields, updateField, valid],
    );
};
